import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Breadcrumbs,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material";
import getApiBase from "../../../components/getApiBase";
import { adminV2Theme as t } from "../theme";
import { VaultGroupTile, VaultSearchShell } from "./DevHubPage";
import { VaultMarkdownView, isDocumentLikeEntry } from "./VaultMarkdownView";
import { printVaultDocumentOnly } from "./vaultPrintHtml";

const ENTRY_TYPES = [
  "variable",
  "secret",
  "credential_set",
  "howto",
  "technical_ref",
  "document",
  "note_plan",
  "media",
  "registry",
];

const ENVIRONMENTS = ["all", "local", "preview", "preprod", "production"];

function secretLike(type) {
  return type === "secret" || type === "variable" || type === "credential_set";
}

function EntryModal({
  open,
  onClose,
  groupKey,
  entryId,
  onSaved,
  startInEdit = false,
  onRequestEdit,
  onRequestRead,
}) {
  const isCreate = !entryId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [canWrite, setCanWrite] = useState(true);
  const [canReveal, setCanReveal] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [tab, setTab] = useState(0);
  /** read = formatted document (share default); edit = vault form */
  const [uiMode, setUiMode] = useState(startInEdit || !entryId ? "edit" : "read");
  /** In edit mode: markdown source vs formatted preview for body */
  const [bodyView, setBodyView] = useState("markdown");
  const [form, setForm] = useState({
    entry_type: "secret",
    title: "",
    purpose: "",
    where_used: "",
    change_policy: "editable",
    sensitivity: "confidential",
    tags: "",
    body_markdown: "",
  });
  const [values, setValues] = useState([]);
  const [valueDrafts, setValueDrafts] = useState({ all: "" });
  const [revealed, setRevealed] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [attachTitle, setAttachTitle] = useState("");
  const [attachFileName, setAttachFileName] = useState("");
  const [attachB64, setAttachB64] = useState("");
  const [resolvedGroupKey, setResolvedGroupKey] = useState(groupKey || "");
  const [shareMsg, setShareMsg] = useState("");
  const [rightsOpen, setRightsOpen] = useState(false);
  const [pendingSensitivity, setPendingSensitivity] = useState("internal");
  const [savingRights, setSavingRights] = useState(false);
  const printRootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (isCreate || startInEdit || secretLike(form.entry_type)) {
      setUiMode("edit");
    } else if (isDocumentLikeEntry(form.entry_type)) {
      setUiMode("read");
    } else {
      setUiMode(startInEdit ? "edit" : "read");
    }
    // only react to open / startInEdit / entryId — form.entry_type applied after load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entryId, startInEdit, isCreate]);

  const load = useCallback(async () => {
    if (!entryId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/entries/${entryId}`, {
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Failed (${res.status})`);
      const e = body.entry;
      setForm({
        entry_type: e.entry_type,
        title: e.title || "",
        purpose: e.purpose || "",
        where_used: e.where_used || "",
        change_policy: e.change_policy || "editable",
        sensitivity: e.sensitivity || "internal",
        tags: (e.tags || []).join(", "),
        body_markdown: e.body_markdown || "",
      });
      setValues(body.values || []);
      setAttachments(body.attachments || []);
      setCanWrite(Boolean(body.can_write));
      setCanReveal(Boolean(body.can_reveal));
      setCanDelete(Boolean(body.can_delete));
      setResolvedGroupKey(e.group?.key || groupKey || "");
      setRevealed({});
      if (startInEdit || secretLike(e.entry_type) || !isDocumentLikeEntry(e.entry_type)) {
        setUiMode("edit");
      } else {
        setUiMode("read");
      }
      setBodyView("markdown");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entryId, groupKey, startInEdit]);

  useEffect(() => {
    if (open && entryId) load();
    if (open && !entryId) {
      setForm({
        entry_type: "secret",
        title: "",
        purpose: "",
        where_used: "",
        change_policy: "editable",
        sensitivity: "confidential",
        tags: "",
        body_markdown: "",
      });
      setValues([]);
      setValueDrafts({ all: "" });
      setAttachments([]);
      setError("");
      setMessage("");
      setShareMsg("");
      setTab(0);
      setResolvedGroupKey(groupKey || "");
    }
  }, [open, entryId, load, groupKey]);

  const copyShareLink = async () => {
    const key = resolvedGroupKey || groupKey;
    if (!entryId || !key) {
      setShareMsg("Save the entry first, then share.");
      return;
    }
    // Share always opens formatted Read view (no ?edit=)
    const url = `${window.location.origin}/admin/v2/dev/vault/${encodeURIComponent(key)}?entry=${entryId}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareMsg(
        "Link copied. Recipients see the formatted document (login + vault access required). Writers use Edit document from that view.",
      );
    } catch {
      setShareMsg(url);
    }
  };

  const downloadAttachment = async (attachmentId) => {
    const res = await fetch(
      `${getApiBase()}/api/admin/v2/vault/entries/${entryId}/attachments/${attachmentId}/url`,
      { credentials: "include" },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Download link failed");
    window.open(body.url, "_blank", "noopener,noreferrer");
  };

  const saveMeta = async (sensitivityOverride) => {
    setError("");
    setMessage("");
    const sensitivity =
      sensitivityOverride != null ? sensitivityOverride : form.sensitivity || "internal";
    const payload = {
      ...form,
      sensitivity,
      tags: form.tags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    if (isCreate) {
      const createBody = {
        group_key: groupKey,
        ...payload,
        values: secretLike(form.entry_type)
          ? Object.entries(valueDrafts)
              .filter(([, v]) => v)
              .map(([environment, value]) => ({ environment, value }))
          : undefined,
      };
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/entries`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createBody),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Create failed");
      setForm((f) => ({ ...f, sensitivity }));
      setMessage("Created");
      onSaved?.(body.entry?.id);
      onClose();
      return;
    }
    const res = await fetch(`${getApiBase()}/api/admin/v2/vault/entries/${entryId}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Save failed");
    setForm((f) => ({ ...f, sensitivity }));
    setMessage("Saved");
    onSaved?.();
  };

  const openRightsPrompt = () => {
    if (!form.title.trim()) return;
    setPendingSensitivity(form.sensitivity || "internal");
    setRightsOpen(true);
  };

  const confirmRightsAndSave = async () => {
    setSavingRights(true);
    try {
      await saveMeta(pendingSensitivity);
      setRightsOpen(false);
    } catch (e) {
      setError(e.message || "Save failed");
      setRightsOpen(false);
    } finally {
      setSavingRights(false);
    }
  };

  const saveValues = async () => {
    const items = Object.entries(valueDrafts)
      .filter(([, v]) => v !== "")
      .map(([environment, value]) => ({ environment, value }));
    if (!items.length) return;
    const res = await fetch(`${getApiBase()}/api/admin/v2/vault/entries/${entryId}/values`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values: items }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Value save failed");
    setValueDrafts({});
    setMessage("Values updated");
    await load();
    onSaved?.();
  };

  const reveal = async (environment) => {
    const res = await fetch(`${getApiBase()}/api/admin/v2/vault/entries/${entryId}/reveal`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ environment }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Reveal failed");
    setRevealed((r) => ({ ...r, [environment]: body.value }));
  };

  const remove = async () => {
    if (!window.confirm("Soft-delete this vault entry?")) return;
    const res = await fetch(`${getApiBase()}/api/admin/v2/vault/entries/${entryId}`, {
      method: "DELETE",
      credentials: "include",
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Delete failed");
    onSaved?.();
    onClose();
  };

  const addAttachmentMeta = async () => {
    if (!attachFileName) return;
    const res = await fetch(
      `${getApiBase()}/api/admin/v2/vault/entries/${entryId}/attachments`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          file_name: attachFileName,
          title: attachTitle || attachFileName,
          content_base64: attachB64 || undefined,
          storage_path: attachB64 ? undefined : `manual/${attachFileName}`,
          mime_type: attachFileName.endsWith(".pdf")
            ? "application/pdf"
            : attachFileName.match(/\.(png|jpg|jpeg|svg)$/i)
              ? "image/*"
              : "text/plain",
        }),
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Attachment failed");
    setAttachTitle("");
    setAttachFileName("");
    setAttachB64("");
    setMessage("Attachment recorded");
    await load();
  };

  const onPickFile = (file) => {
    if (!file) return;
    setAttachFileName(file.name);
    if (!attachTitle) setAttachTitle(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const b64 = result.includes(",") ? result.split(",")[1] : result;
      setAttachB64(b64);
    };
    reader.readAsDataURL(file);
  };

  const run = async (fn) => {
    try {
      setError("");
      await fn();
    } catch (e) {
      setError(e.message || "Action failed");
    }
  };

  const showDocumentRead =
    !isCreate && !loading && uiMode === "read" && isDocumentLikeEntry(form.entry_type);

  const goEdit = () => {
    setUiMode("edit");
    setTab(0);
    if (onRequestEdit) onRequestEdit();
  };

  const printDocument = () => {
    printVaultDocumentOnly(printRootRef.current, form.title || "Vault document");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          "@media print": { display: "none" },
        }}
      >
        <span>{isCreate ? "New vault entry" : form.title || "Vault entry"}</span>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {!isCreate && (
            <Button
              size="small"
              onClick={() => run(copyShareLink)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Share / copy link
            </Button>
          )}
          {showDocumentRead && (
            <Button
              size="small"
              onClick={printDocument}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Print
            </Button>
          )}
          {showDocumentRead && canWrite && (
            <Button
              size="small"
              variant="contained"
              onClick={goEdit}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                background: t.colors.primary,
                "&:hover": { background: t.colors.primaryHover },
              }}
            >
              Edit document
            </Button>
          )}
          {!showDocumentRead && !isCreate && isDocumentLikeEntry(form.entry_type) && (
            <Button
              size="small"
              onClick={() => {
                setUiMode("read");
                if (onRequestRead) onRequestRead();
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              View as document
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : showDocumentRead ? (
          <Box ref={printRootRef} className="vault-print-root">
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} className="no-print">
                {error}
              </Alert>
            )}
            {shareMsg && (
              <Alert
                severity="info"
                className="no-print"
                sx={{ mb: 2 }}
                onClose={() => setShareMsg("")}
              >
                {shareMsg}
              </Alert>
            )}
            <Typography className="print-title" sx={{ fontSize: 24, fontWeight: 700, mb: 1, lineHeight: 1.25 }}>
              {form.title || "Untitled"}
            </Typography>
            {form.purpose && (
              <Typography className="print-purpose" sx={{ fontSize: 14, color: t.colors.textMuted, mb: 2 }}>
                {form.purpose}
              </Typography>
            )}
            <VaultMarkdownView markdown={form.body_markdown} />
            {attachments.length > 0 && (
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${t.colors.border}` }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Files</Typography>
                {attachments.map((a) => (
                  <Box
                    key={a.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      py: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>
                      {a.title || a.file_name}
                      <Typography component="span" sx={{ color: t.colors.textMuted, ml: 1 }}>
                        {a.mime_type || "file"}
                      </Typography>
                    </Typography>
                    {a.has_file && (
                      <Button
                        size="small"
                        className="no-print"
                        sx={{ textTransform: "none" }}
                        onClick={() => run(() => downloadAttachment(a.id))}
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {message && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}
            {shareMsg && (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShareMsg("")}>
                {shareMsg}
              </Alert>
            )}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Details" sx={{ textTransform: "none" }} />
              <Tab
                label="Values"
                sx={{ textTransform: "none" }}
                disabled={!secretLike(form.entry_type)}
              />
              <Tab label="Attachments" sx={{ textTransform: "none" }} disabled={isCreate} />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Type"
                  disabled={!isCreate || !canWrite}
                  value={form.entry_type}
                  onChange={(e) => setForm((f) => ({ ...f, entry_type: e.target.value }))}
                >
                  {ENTRY_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Title"
                  disabled={!canWrite}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
                <TextField
                  size="small"
                  label="Purpose"
                  disabled={!canWrite}
                  value={form.purpose}
                  onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
                />
                <TextField
                  size="small"
                  label="Where used"
                  disabled={!canWrite}
                  value={form.where_used}
                  onChange={(e) => setForm((f) => ({ ...f, where_used: e.target.value }))}
                />
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <TextField
                    select
                    size="small"
                    label="Change policy"
                    disabled={!canWrite}
                    value={form.change_policy}
                    onChange={(e) => setForm((f) => ({ ...f, change_policy: e.target.value }))}
                  >
                    {["editable", "locked", "reveal_only"].map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Sensitivity"
                    disabled={!canWrite}
                    value={form.sensitivity}
                    onChange={(e) => setForm((f) => ({ ...f, sensitivity: e.target.value }))}
                    helperText={
                      form.sensitivity === "restricted"
                        ? "Only vault:restricted (or strategy/admin) — e.g. managerial plans"
                        : form.sensitivity === "confidential"
                          ? "Needs vault:confidential — not everyone with library access"
                          : "Anyone who can open this library"
                    }
                  >
                    {["internal", "confidential", "restricted"].map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <TextField
                  size="small"
                  label="Tags (comma-separated)"
                  disabled={!canWrite}
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                />
                {isDocumentLikeEntry(form.entry_type) && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Body view:</Typography>
                    <Button
                      size="small"
                      variant={bodyView === "markdown" ? "contained" : "outlined"}
                      disabled={!canWrite && bodyView !== "markdown"}
                      onClick={() => setBodyView("markdown")}
                      sx={{ textTransform: "none" }}
                    >
                      Markdown
                    </Button>
                    <Button
                      size="small"
                      variant={bodyView === "formatted" ? "contained" : "outlined"}
                      onClick={() => setBodyView("formatted")}
                      sx={{ textTransform: "none" }}
                    >
                      Formatted
                    </Button>
                  </Box>
                )}
                {isDocumentLikeEntry(form.entry_type) && bodyView === "formatted" ? (
                  <Box
                    sx={{
                      p: 2,
                      border: `1px solid ${t.colors.border}`,
                      borderRadius: `${t.radius.md}px`,
                      background: t.colors.surface,
                      minHeight: 160,
                    }}
                  >
                    <VaultMarkdownView markdown={form.body_markdown} />
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    label="Body (markdown) — howto / technical_ref / notes"
                    disabled={!canWrite}
                    multiline
                    minRows={6}
                    value={form.body_markdown}
                    onChange={(e) => setForm((f) => ({ ...f, body_markdown: e.target.value }))}
                  />
                )}
                {isCreate && secretLike(form.entry_type) && (
                  <TextField
                    size="small"
                    label="Initial value (shared / all envs)"
                    type="password"
                    value={valueDrafts.all || ""}
                    onChange={(e) => setValueDrafts((d) => ({ ...d, all: e.target.value }))}
                    helperText="Stored encrypted. Per-env overrides after create."
                  />
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
                  Shared default is environment <code>all</code>. Overrides win per env. Reveal is
                  audited.
                </Typography>
                {values.map((v) => (
                  <Box
                    key={v.environment}
                    sx={{
                      p: 1.5,
                      border: `1px solid ${t.colors.border}`,
                      borderRadius: `${t.radius.sm}px`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
                      {v.environment}{" "}
                      <Typography component="span" sx={{ color: t.colors.textMuted, fontWeight: 400 }}>
                        {v.has_value ? v.value_mask_hint || "(set)" : "(empty)"}
                      </Typography>
                    </Typography>
                    {revealed[v.environment] && (
                      <TextField
                        size="small"
                        fullWidth
                        value={revealed[v.environment]}
                        InputProps={{ readOnly: true }}
                        sx={{ mb: 1 }}
                      />
                    )}
                    {canReveal && (
                      <Button size="small" sx={{ textTransform: "none" }} onClick={() => run(() => reveal(v.environment))}>
                        Reveal
                      </Button>
                    )}
                  </Box>
                ))}
                {ENVIRONMENTS.map((env) => (
                  <TextField
                    key={env}
                    size="small"
                    label={`Set / override: ${env}`}
                    type="password"
                    value={valueDrafts[env] || ""}
                    onChange={(e) => setValueDrafts((d) => ({ ...d, [env]: e.target.value }))}
                    disabled={!canWrite}
                  />
                ))}
                {canWrite && (
                  <Button
                    variant="outlined"
                    onClick={() => run(saveValues)}
                    sx={{ textTransform: "none", alignSelf: "start" }}
                  >
                    Save values
                  </Button>
                )}
              </Box>
            )}

            {tab === 2 && (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {attachments.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
                    No attachments. Upload requires private Supabase bucket <code>platform-vault</code>,
                    or register a manual storage_path.
                  </Typography>
                )}
                {attachments.map((a) => (
                  <Box
                    key={a.id}
                    sx={{ p: 1.25, border: `1px solid ${t.colors.border}`, borderRadius: `${t.radius.sm}px` }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{a.title || a.file_name}</Typography>
                    <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
                      {a.mime_type || "file"}
                      {a.caption ? ` — ${a.caption}` : ""}
                    </Typography>
                    {a.has_file && (
                      <Button
                        size="small"
                        sx={{ textTransform: "none", mt: 0.5 }}
                        onClick={() => run(() => downloadAttachment(a.id))}
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                ))}
                {canWrite && (
                  <>
                    <TextField
                      size="small"
                      label="Attachment title"
                      value={attachTitle}
                      onChange={(e) => setAttachTitle(e.target.value)}
                    />
                    <Button component="label" variant="outlined" sx={{ textTransform: "none", alignSelf: "start" }}>
                      Choose file
                      <input
                        type="file"
                        hidden
                        onChange={(e) => onPickFile(e.target.files?.[0])}
                      />
                    </Button>
                    {attachFileName && (
                      <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
                        Selected: {attachFileName}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      disabled={!attachFileName}
                      onClick={() => run(addAttachmentMeta)}
                      sx={{
                        textTransform: "none",
                        background: t.colors.primary,
                        alignSelf: "start",
                        "&:hover": { background: t.colors.primaryHover },
                      }}
                    >
                      Add attachment
                    </Button>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, justifyContent: "space-between", "@media print": { display: "none" } }}
      >
        <Box>
          {!isCreate && canDelete && !showDocumentRead && (
            <Button color="error" onClick={() => run(remove)} sx={{ textTransform: "none" }}>
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: "none", color: t.colors.textMuted }}>
            Close
          </Button>
          {showDocumentRead && canWrite && (
            <Button
              variant="contained"
              onClick={goEdit}
              sx={{
                textTransform: "none",
                background: t.colors.primary,
                fontWeight: 600,
                "&:hover": { background: t.colors.primaryHover },
              }}
            >
              Edit document
            </Button>
          )}
          {canWrite && !showDocumentRead && tab === 0 && (
            <Button
              variant="contained"
              onClick={openRightsPrompt}
              disabled={!form.title.trim()}
              sx={{
                textTransform: "none",
                background: t.colors.primary,
                fontWeight: 600,
                "&:hover": { background: t.colors.primaryHover },
              }}
            >
              {isCreate ? "Create" : "Save"}
            </Button>
          )}
        </Box>
      </DialogActions>

      <Dialog open={rightsOpen} onClose={() => !savingRights && setRightsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Who can open this item?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: t.colors.textMuted, mb: 2 }}>
            Library access alone is not enough for every article. Choose rights now — two documents
            in the same library can have different audiences.
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
              Access level
            </FormLabel>
            <RadioGroup
              value={pendingSensitivity}
              onChange={(e) => setPendingSensitivity(e.target.value)}
            >
              <FormControlLabel
                value="internal"
                control={<Radio />}
                label="Internal — anyone who can open this library"
              />
              <FormControlLabel
                value="confidential"
                control={<Radio />}
                label="Confidential — needs vault:confidential (not everyone with library access)"
              />
              <FormControlLabel
                value="restricted"
                control={<Radio />}
                label="Restricted — managerial / highly private (vault:restricted only)"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRightsOpen(false)}
            disabled={savingRights}
            sx={{ textTransform: "none", color: t.colors.textMuted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingRights}
            onClick={() => run(confirmRightsAndSave)}
            sx={{
              textTransform: "none",
              background: t.colors.primary,
              fontWeight: 600,
              "&:hover": { background: t.colors.primaryHover },
            }}
          >
            {savingRights ? "Saving…" : isCreate ? "Save with these rights" : "Update rights & save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function DossierModal({ open, onClose, q, onOpenEntry }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !q) return;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${getApiBase()}/api/admin/v2/vault/search?q=${encodeURIComponent(q)}`,
          { credentials: "include" },
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || "Search failed");
        setData(body);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [open, q]);

  const sections = data?.sections || {};

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>Dossier: {q}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && data && (
          <>
            <Typography sx={{ fontSize: 13, color: t.colors.textMuted, mb: 2 }}>
              {data.total_entries} entries
              {data.collections?.length ? ` · ${data.collections.length} collections` : ""}
            </Typography>
            {Object.keys(sections).length === 0 && (
              <Typography sx={{ color: t.colors.textMuted }}>No matches.</Typography>
            )}
            {Object.entries(sections).map(([type, rows]) => (
              <Box key={type} sx={{ mb: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, textTransform: "capitalize" }}>
                  {type.replace("_", " ")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {rows.map((row) => (
                    <Box
                      key={row.id}
                      component="button"
                      type="button"
                      onClick={() => onOpenEntry(row)}
                      sx={{
                        textAlign: "left",
                        border: `1px solid ${t.colors.border}`,
                        borderRadius: `${t.radius.sm}px`,
                        p: 1.25,
                        background: t.colors.surface,
                        cursor: "pointer",
                        fontFamily: t.font.family,
                        "&:hover": { borderColor: t.colors.primary },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{row.title}</Typography>
                      <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
                        {row.group_name}
                        {row.purpose ? ` — ${row.purpose}` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
            {(data.collections || []).length > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Collections</Typography>
                {(data.collections || []).map((c) => (
                  <Chip key={c.key} label={c.title} sx={{ mr: 1, mb: 1 }} />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function VaultLobbyPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groups, setGroups] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [q, setQ] = useState("");
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierQ, setDossierQ] = useState("");
  const [entryModal, setEntryModal] = useState({ open: false, id: null, groupKey: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/vault/groups`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (${res.status})`);
      }
      const data = await res.json();
      setGroups(data.groups || []);
      setPinned(data.pinned_collections || []);
    } catch (e) {
      setError(e.message || "Failed to load vault lobby");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: t.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 13 }}>
        <Link to="/admin/v2/dev" style={{ color: t.colors.primary, textDecoration: "none" }}>
          Development & Health
        </Link>
        <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>Vault lobby</Typography>
      </Breadcrumbs>

      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
        Platform vault
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, mb: 1.5, fontSize: 14, maxWidth: 720 }}>
        Two kinds of places — do not store the same secret in both.
      </Typography>
      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: `${t.radius.md}px`,
          border: `1px solid ${t.colors.border}`,
          background: t.colors.surface,
          maxWidth: 720,
        }}
      >
        <Typography sx={{ fontSize: 13, color: t.colors.text, mb: 1, fontWeight: 600 }}>
          Environment inventories — where values live
        </Typography>
        <Typography sx={{ fontSize: 13, color: t.colors.textMuted, mb: 1.5 }}>
          Backend ENV, Frontend ENV, Vercel Backend/Frontend ENV, Railway ENV. Requires{" "}
          <code>vault:env:read</code> (and <code>vault:env:reveal</code> to see secret values).
          Separate from library access — a howto link does not unlock passwords.
        </Typography>
        <Typography sx={{ fontSize: 13, color: t.colors.text, mb: 1, fontWeight: 600 }}>
          Libraries — how systems work
        </Typography>
        <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
          Calendar, Stripe, Database, Voice, and similar. Education and variable names. Requires{" "}
          <code>vault:read</code> (or a topic permission). Libraries link into ENV when you need a
          value — they do not keep a second copy of the secret.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <VaultSearchShell
        placeholder="Search vault (dossier)…"
        value={q}
        onChange={setQ}
        onSubmit={() => {
          if (q.trim().length >= 2) {
            setDossierQ(q.trim());
            setDossierOpen(true);
          }
        }}
      />

      {pinned.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: t.colors.textMuted, mb: 1 }}>
            Pinned collections
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {pinned.map((c) => (
              <Chip
                key={c.key}
                label={c.title}
                onClick={() => {
                  const g = groups.find((x) => x.id === c.group_id);
                  if (g) navigate(`/admin/v2/dev/vault/${g.key}`);
                }}
                sx={{ background: t.colors.primarySoft, fontWeight: 600, cursor: "pointer" }}
              />
            ))}
          </Box>
        </Box>
      )}

      {(() => {
        const sectionOf = (g) =>
          g.lobby_section ||
          (String(g.key || "").startsWith("env_") ? "env_inventory" : "topic");
        const envGroups = groups.filter((g) => sectionOf(g) === "env_inventory");
        const libraryGroups = groups.filter((g) => sectionOf(g) !== "env_inventory");
        const renderGrid = (list) => (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 2,
              mb: 3,
            }}
          >
            {list.map((g) => (
              <VaultGroupTile
                key={g.key}
                group={g}
                onOpen={(key) => navigate(`/admin/v2/dev/vault/${key}`)}
              />
            ))}
          </Box>
        );
        return (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }}>
              Environment inventories
            </Typography>
            <Typography sx={{ fontSize: 12, color: t.colors.textMuted, mb: 1.5 }}>
              Full lists + values (Backend, Frontend, Vercel, Railway)
            </Typography>
            {envGroups.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                ENV inventory folders not loaded yet. Apply migration{" "}
                <code>018_platform_vault_env_inventories.sql</code> on this database, then refresh.
              </Alert>
            ) : (
              renderGrid(envGroups)
            )}

            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }}>Libraries</Typography>
            <Typography sx={{ fontSize: 12, color: t.colors.textMuted, mb: 1.5 }}>
              Topic guides — names and how-to; open an ENV inventory for values
            </Typography>
            {renderGrid(libraryGroups)}
          </>
        );
      })()}

      <DossierModal
        open={dossierOpen}
        q={dossierQ}
        onClose={() => setDossierOpen(false)}
        onOpenEntry={(row) => {
          setDossierOpen(false);
          if (row.group_key) {
            navigate(`/admin/v2/dev/vault/${row.group_key}?entry=${row.id}`);
          } else {
            setEntryModal({ open: true, id: row.id, groupKey: null });
          }
        }}
      />
      <EntryModal
        open={entryModal.open}
        entryId={entryModal.id}
        groupKey={entryModal.groupKey}
        onClose={() => setEntryModal({ open: false, id: null, groupKey: null })}
        onSaved={load}
      />
    </Box>
  );
}

export function VaultGroupPage() {
  const { groupKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [group, setGroup] = useState(null);
  const [entries, setEntries] = useState([]);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [modal, setModal] = useState({ open: false, id: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (typeFilter) params.set("type", typeFilter);
      const highlight = searchParams.get("highlight");
      if (highlight) params.set("highlight", highlight);
      const tag = searchParams.get("tag");
      if (tag) params.set("tag", tag);
      const qs = params.toString() ? `?${params}` : "";
      const res = await fetch(
        `${getApiBase()}/api/admin/v2/vault/groups/${encodeURIComponent(groupKey)}${qs}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (${res.status})`);
      }
      const data = await res.json();
      setGroup(data.group);
      setEntries(data.entries || []);
      setCollections(data.collections || []);
    } catch (e) {
      setError(e.message || "Failed to load sub-vault");
    } finally {
      setLoading(false);
    }
  }, [groupKey, q, typeFilter, searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const entryParam = searchParams.get("entry");
    if (entryParam) {
      setModal({ open: true, id: Number(entryParam) });
    }
  }, [searchParams]);

  const openEntryRead = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("entry", String(id));
    next.delete("edit");
    setSearchParams(next, { replace: true });
    setModal({ open: true, id: Number(id) });
  };

  const openNewEntry = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("entry");
    next.delete("edit");
    setSearchParams(next, { replace: true });
    setModal({ open: true, id: null });
  };

  if (loading && !group) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: t.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 13 }}>
        <Link to="/admin/v2/dev" style={{ color: t.colors.primary, textDecoration: "none" }}>
          Development & Health
        </Link>
        <Link to="/admin/v2/dev/vault" style={{ color: t.colors.primary, textDecoration: "none" }}>
          Vault lobby
        </Link>
        <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
          {group?.display_name || groupKey}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
        <Box>
          <Chip
            size="small"
            label={
              String(groupKey || "").startsWith("env_") || group?.lobby_section === "env_inventory"
                ? "Environment inventory — values live here"
                : "Library — guides & names (values live in ENV)"
            }
            sx={{
              mb: 1,
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              background:
                String(groupKey || "").startsWith("env_") || group?.lobby_section === "env_inventory"
                  ? t.colors.successSoft
                  : t.colors.accentSoft,
            }}
          />
          <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
            {group?.display_name || groupKey}
          </Typography>
          <Typography sx={{ color: t.colors.textMuted, fontSize: 14, maxWidth: 640 }}>
            {group?.description || ""}
          </Typography>
          {!(
            String(groupKey || "").startsWith("env_") || group?.lobby_section === "env_inventory"
          ) && (
            <Typography sx={{ fontSize: 13, mt: 1, maxWidth: 640 }}>
              Need a value? Open{" "}
              <Link to="/admin/v2/dev/vault/env_backend" style={{ color: t.colors.primary }}>
                Backend ENV
              </Link>
              {" · "}
              <Link to="/admin/v2/dev/vault/env_frontend" style={{ color: t.colors.primary }}>
                Frontend ENV
              </Link>
              {" · "}
              <Link to="/admin/v2/dev/vault" style={{ color: t.colors.primary }}>
                all inventories
              </Link>
              . Do not paste secrets into this library.
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={openNewEntry}
          sx={{
            textTransform: "none",
            background: t.colors.primary,
            fontWeight: 600,
            height: 40,
            "&:hover": { background: t.colors.primaryHover },
          }}
        >
          New entry
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start", mb: 2 }}>
        <VaultSearchShell
          placeholder={`Filter in ${group?.display_name || "group"}…`}
          value={q}
          onChange={setQ}
          onSubmit={load}
          helperText="Scoped filter (client + API)"
        />
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { background: t.colors.surface } }}
        >
          <MenuItem value="">All types</MenuItem>
          {ENTRY_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {collections.length > 0 && (
        <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {collections.map((c) => (
            <Chip
              key={c.key}
              label={c.title}
              size="small"
              sx={{ background: t.colors.accentSoft, fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {entries.length === 0 ? (
        <Box
          sx={{
            border: `1px dashed ${t.colors.border}`,
            borderRadius: `${t.radius.md}px`,
            p: 3,
            background: t.colors.surface,
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No entries yet</Typography>
          <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
            Create a variable, secret, howto, or technical_ref to start filling this sub-vault.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {entries.map((e) => (
            <Box
              key={e.id}
              component="button"
              type="button"
              onClick={() => openEntryRead(e.id)}
              sx={{
                textAlign: "left",
                p: 1.5,
                border: `1px solid ${t.colors.border}`,
                borderRadius: `${t.radius.sm}px`,
                background: t.colors.surface,
                cursor: "pointer",
                fontFamily: t.font.family,
                "&:hover": { borderColor: t.colors.primary },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{e.title}</Typography>
                <Chip label={e.entry_type} size="small" sx={{ height: 22, fontSize: 11 }} />
              </Box>
              <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>
                {e.purpose || e.change_policy}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <EntryModal
        open={modal.open}
        entryId={modal.id}
        groupKey={groupKey}
        startInEdit={Boolean(modal.id == null || searchParams.get("edit") === "1")}
        onRequestEdit={() => {
          const next = new URLSearchParams(searchParams);
          if (modal.id) next.set("entry", String(modal.id));
          next.set("edit", "1");
          setSearchParams(next, { replace: true });
        }}
        onRequestRead={() => {
          const next = new URLSearchParams(searchParams);
          next.delete("edit");
          setSearchParams(next, { replace: true });
        }}
        onClose={() => {
          setModal({ open: false, id: null });
          if (searchParams.get("entry") || searchParams.get("edit")) {
            const next = new URLSearchParams(searchParams);
            next.delete("entry");
            next.delete("edit");
            setSearchParams(next, { replace: true });
          }
        }}
        onSaved={load}
      />
    </Box>
  );
}
